import { Controller } from "@hotwired/stimulus";
import { patch } from "@rails/request.js";

const ZOOM_LEVELS = [0.5, 0.75, 1.0, 1.25, 1.5];

/**
 * Handles drag-and-drop of tables on the floor plan grid
 * Uses native HTML5 drag and drop API for cross-container support
 * Optimistic updates - moves element immediately, reloads on error
 *
 * Zoom works by changing cell size while keeping viewport fixed:
 * - Zoom out (scale < 1): smaller cells, more cells visible, see more of floor
 * - Zoom in (scale > 1): bigger cells, fewer cells visible, more detail
 */
export default class extends Controller {
  static targets = ["grid", "viewport", "zoomLevel"];
  static values = {
    floorId: Number,
    gridCols: { type: Number, default: 12 },
    gridRows: { type: Number, default: 8 },
    baseCellSize: { type: Number, default: 80 },
    zoom: { type: Number, default: 1.0 },
  };

  connect() {
    this.draggedElement = null;
    this.originalPosition = null;
    this.dropSucceeded = false;
    this.setupDropZone();
    this.setupDragTracking();
    // Initial render to fill viewport
    this.applyZoom();
  }

  disconnect() {
    // Clean up document-level event listeners
    if (this.boundDragStart) {
      document.removeEventListener("dragstart", this.boundDragStart);
    }
    if (this.boundDragEnd) {
      document.removeEventListener("dragend", this.boundDragEnd);
    }
  }

  get cellSize() {
    return this.baseCellSizeValue * this.zoomValue;
  }

  get visibleCols() {
    // Calculate based on actual viewport width
    if (!this.hasViewportTarget) {
      return Math.ceil(this.gridColsValue / this.zoomValue);
    }
    return Math.ceil(this.viewportTarget.clientWidth / this.cellSize);
  }

  get visibleRows() {
    // Calculate based on actual viewport height
    if (!this.hasViewportTarget) {
      return Math.ceil(this.gridRowsValue / this.zoomValue);
    }
    return Math.ceil(this.viewportTarget.clientHeight / this.cellSize);
  }

  zoomIn() {
    const currentIndex = ZOOM_LEVELS.indexOf(this.zoomValue);
    if (currentIndex < ZOOM_LEVELS.length - 1) {
      this.zoomValue = ZOOM_LEVELS[currentIndex + 1];
      this.applyZoom();
      this.saveZoom();
    }
  }

  zoomOut() {
    const currentIndex = ZOOM_LEVELS.indexOf(this.zoomValue);
    if (currentIndex > 0) {
      this.zoomValue = ZOOM_LEVELS[currentIndex - 1];
      this.applyZoom();
      this.saveZoom();
    }
  }

  async saveZoom() {
    const venueId = this.getVenueIdFromUrl();
    const url = `/venues/${venueId}/floor_plan/floors/${this.floorIdValue}/zoom`;

    try {
      await patch(url, {
        body: { zoom: this.zoomValue },
      });
    } catch (error) {
      console.error("Error saving zoom:", error);
    }
  }

  applyZoom() {
    if (!this.hasGridTarget || !this.hasViewportTarget) return;

    const cellSize = this.cellSize;
    const viewportWidth = this.viewportTarget.clientWidth;
    const viewportHeight = this.viewportTarget.clientHeight;

    // Find the furthest table position to determine required grid size
    let requiredCols = 0;
    let requiredRows = 0;

    this.gridTarget.querySelectorAll("[data-table-id][data-grid-width]").forEach((table) => {
      const gridX = parseInt(table.dataset.gridX) || 0;
      const gridY = parseInt(table.dataset.gridY) || 0;
      const gridWidth = parseInt(table.dataset.gridWidth) || 1;
      const gridHeight = parseInt(table.dataset.gridHeight) || 1;

      requiredCols = Math.max(requiredCols, gridX + gridWidth);
      requiredRows = Math.max(requiredRows, gridY + gridHeight);
    });

    // Grid matches viewport exactly unless tables extend beyond
    const requiredWidth = requiredCols * cellSize;
    const requiredHeight = requiredRows * cellSize;
    const gridWidth = requiredWidth > viewportWidth ? requiredWidth : viewportWidth;
    const gridHeight = requiredHeight > viewportHeight ? requiredHeight : viewportHeight;

    this.gridTarget.style.width = `${gridWidth}px`;
    this.gridTarget.style.height = `${gridHeight}px`;

    // Calculate cols/rows for grid lines based on actual grid size
    const maxCol = Math.ceil(gridWidth / cellSize);
    const maxRow = Math.ceil(gridHeight / cellSize);

    // Update cell size data attribute for child controllers
    this.gridTarget.dataset.cellSize = cellSize;

    // Regenerate grid lines for entire grid area
    this.renderGridLines(maxCol, maxRow, cellSize);

    // Update all table positions and sizes
    this.gridTarget.querySelectorAll("[data-table-id][data-grid-width]").forEach((table) => {
      const gridX = parseInt(table.dataset.gridX) || 0;
      const gridY = parseInt(table.dataset.gridY) || 0;
      const gridWidth = parseInt(table.dataset.gridWidth) || 1;
      const gridHeight = parseInt(table.dataset.gridHeight) || 1;

      table.style.left = `${gridX * cellSize + 4}px`;
      table.style.top = `${gridY * cellSize + 4}px`;
      table.style.width = `${gridWidth * cellSize - 8}px`;
      table.style.height = `${gridHeight * cellSize - 8}px`;
    });

    // Update zoom level display
    if (this.hasZoomLevelTarget) {
      this.zoomLevelTarget.textContent = `${Math.round(this.zoomValue * 100)}%`;
    }
  }

  renderGridLines(cols, rows, cellSize) {
    // Remove existing grid lines
    this.gridTarget
      .querySelectorAll(".grid-line")
      .forEach((line) => line.remove());

    // Vertical lines
    for (let i = 1; i < cols; i++) {
      const line = document.createElement("div");
      line.className = "grid-line absolute top-0 bottom-0 w-px bg-base-300/50";
      line.style.left = `${i * cellSize}px`;
      this.gridTarget.appendChild(line);
    }

    // Horizontal lines
    for (let i = 1; i < rows; i++) {
      const line = document.createElement("div");
      line.className = "grid-line absolute left-0 right-0 h-px bg-base-300/50";
      line.style.top = `${i * cellSize}px`;
      this.gridTarget.appendChild(line);
    }
  }

  setupDragTracking() {
    this.boundDragStart = (e) => {
      const tableId = e.target.dataset?.tableId;
      if (!tableId) return;

      // Only track elements within this controller's grid
      const element = this.gridTarget.querySelector(`[data-table-id="${tableId}"]`);
      if (element) {
        this.draggedElement = element;
        this.originalPosition = {
          left: element.style.left,
          top: element.style.top,
          gridX: element.dataset.gridX,
          gridY: element.dataset.gridY,
        };
        this.dropSucceeded = false;
      }
    };

    this.boundDragEnd = () => {
      // Reset position if drag was cancelled (dropped outside valid zones)
      if (this.draggedElement && this.originalPosition && !this.dropSucceeded) {
        this.draggedElement.style.left = this.originalPosition.left;
        this.draggedElement.style.top = this.originalPosition.top;
        this.draggedElement.dataset.gridX = this.originalPosition.gridX;
        this.draggedElement.dataset.gridY = this.originalPosition.gridY;
      }
      this.draggedElement = null;
      this.originalPosition = null;
      this.dropSucceeded = false;
    };

    document.addEventListener("dragstart", this.boundDragStart);
    document.addEventListener("dragend", this.boundDragEnd);
  }

  setupDropZone() {
    if (!this.hasGridTarget) return;

    this.gridTarget.addEventListener("dragover", (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      this.gridTarget.classList.add("ring-2", "ring-primary");

      if (this.draggedElement) {
        const { gridX, gridY } = this.calculateGridPosition(e);
        this.snapToGrid(this.draggedElement, gridX, gridY);
      }
    });

    this.gridTarget.addEventListener("dragleave", (e) => {
      if (!this.gridTarget.contains(e.relatedTarget)) {
        this.gridTarget.classList.remove("ring-2", "ring-primary");
      }
    });

    this.gridTarget.addEventListener("drop", (e) => {
      e.preventDefault();
      this.gridTarget.classList.remove("ring-2", "ring-primary");
      this.dropSucceeded = true;

      const tableId = e.dataTransfer.getData("text/plain");
      if (!tableId) return;

      const { gridX, gridY } = this.calculateGridPosition(e);

      const sourceElement = document.querySelector(`[data-table-id="${tableId}"]`);
      const isRepositioning = sourceElement?.closest("[data-floor-plan-target='grid']");

      if (isRepositioning) {
        this.snapToGrid(sourceElement, gridX, gridY);
        // Recalculate grid size in case table moved beyond current bounds
        this.updateGridSize();
      }

      this.updateTablePosition(tableId, gridX, gridY, !isRepositioning);
    });
  }

  calculateGridPosition(event) {
    const rect = this.gridTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Calculate cols/rows based on current grid dimensions
    const cols = Math.ceil(this.gridTarget.clientWidth / this.cellSize);
    const rows = Math.ceil(this.gridTarget.clientHeight / this.cellSize);

    const gridX = Math.max(0, Math.min(cols - 1, Math.floor(x / this.cellSize)));
    const gridY = Math.max(0, Math.min(rows - 1, Math.floor(y / this.cellSize)));

    return { gridX, gridY };
  }

  snapToGrid(element, gridX, gridY) {
    const left = gridX * this.cellSize + 4;
    const top = gridY * this.cellSize + 4;
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;

    // Update data attributes so applyZoom uses correct position
    element.dataset.gridX = gridX;
    element.dataset.gridY = gridY;
  }

  updateGridSize() {
    if (!this.hasGridTarget || !this.hasViewportTarget) return;

    const cellSize = this.cellSize;
    const viewportWidth = this.viewportTarget.clientWidth;
    const viewportHeight = this.viewportTarget.clientHeight;

    // Find the furthest table position
    let requiredCols = 0;
    let requiredRows = 0;

    this.gridTarget.querySelectorAll("[data-table-id][data-grid-width]").forEach((table) => {
      const gridX = parseInt(table.dataset.gridX) || 0;
      const gridY = parseInt(table.dataset.gridY) || 0;
      const gridWidth = parseInt(table.dataset.gridWidth) || 1;
      const gridHeight = parseInt(table.dataset.gridHeight) || 1;

      requiredCols = Math.max(requiredCols, gridX + gridWidth);
      requiredRows = Math.max(requiredRows, gridY + gridHeight);
    });

    // Grid matches viewport exactly unless tables extend beyond
    const requiredWidth = requiredCols * cellSize;
    const requiredHeight = requiredRows * cellSize;
    const gridWidth = requiredWidth > viewportWidth ? requiredWidth : viewportWidth;
    const gridHeight = requiredHeight > viewportHeight ? requiredHeight : viewportHeight;

    this.gridTarget.style.width = `${gridWidth}px`;
    this.gridTarget.style.height = `${gridHeight}px`;

    // Redraw grid lines
    const maxCol = Math.ceil(gridWidth / cellSize);
    const maxRow = Math.ceil(gridHeight / cellSize);
    this.renderGridLines(maxCol, maxRow, cellSize);
  }

  async updateTablePosition(tableId, gridX, gridY, reloadOnSuccess = false) {
    const venueId = this.getVenueIdFromUrl();
    const url = `/venues/${venueId}/tables/${tableId}/position`;

    try {
      const response = await patch(url, {
        body: {
          admin_venues_tables_positions_update_form: {
            grid_x: gridX,
            grid_y: gridY,
            floor_id: this.floorIdValue,
          },
        },
      });

      if (response.ok) {
        if (reloadOnSuccess) window.location.reload();
      } else {
        console.error("Failed to update table position");
        window.location.reload();
      }
    } catch (error) {
      console.error("Error updating table position:", error);
      window.location.reload();
    }
  }

  getVenueIdFromUrl() {
    const pathParts = window.location.pathname.split("/");
    const venueIndex = pathParts.indexOf("venues");
    return pathParts[venueIndex + 1];
  }
}
